import React, { useCallback, useEffect, useRef, useState } from 'react';
import { debounce, isEmpty, isNil } from 'lodash';
import numbro from 'numbro';
import { TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import { FlowStatus } from 'configuration-schemas/bindings.types';
import { SortBySlice, SubheaderDropdownSlice } from './Subheader.slice';
import SubheaderCheckboxes from './SubheaderCheckboxes';
import SubheaderDropdownMulti from './SubheaderDropdownMulti';
import SubheaderDropdown from './SubheaderDropdown';
import SubheaderQuickActionButton from './SubheaderQuickActionButton';
import { IconButton, MuiThemeProvider, OutlinedInput } from '@material-ui/core';
import { muiTheme } from 'src/utils/Style/Theme';
import { Close } from '@material-ui/icons';
import { Option } from './Subheader';
import { ExtraPivotParam } from 'configuration-schemas/confdefnComponentProps';

export interface FlowStatusInputs {
  showFlowStatus: boolean | undefined;
  flowStatusOptions: FlowStatus;
  flowStatus: number[];
  updateFlowStatus: (values: number[]) => void;
}

export interface AltFlowStatusInputs {
  showAltFlowStatus: boolean | undefined;
  altFlowStatusOptions: FlowStatus;
  altFlowStatus: number[];
  updateAltFlowStatus: (values: number[]) => void;
}

export interface SearchInputs {
  showSearch: boolean | undefined;
  search: string;
  searchReturn: ((searchString: string) => void) | undefined;
  updateSearchString: (searchString: string) => void;
}

export interface AltSearchInputs {
  showAltSearch: boolean | undefined;
  altSearch: string;
  searchReturn: ((searchString: string) => void) | undefined;
  updateAltSearchString: (searchString: string) => void;
}

export interface PareDownInputs {
  showPareDown: boolean;
  selections: TenantConfigViewItem[];
  options: TenantConfigViewItem[];
  setPareDownSelections: (items: TenantConfigViewItem[]) => void;
}

export interface GroupByInputs {
  showGroupBy: boolean;
  groupBy: SubheaderDropdownSlice;
  onChangeGroup: (() => void) | undefined;
  setGroupBySelection: (value: number | null) => void;
}

export interface SortByInputs {
  showSortBy: boolean;
  sortBy: SortBySlice;
  onChangeSort: (() => void) | undefined;
  setSortBySelection: (value: number | null) => void;
}

export interface SortArrowInputs {
  showSortArrow: boolean;
  onChangeSort: (() => void) | undefined;
  setSortByDirection: () => void;
}

export interface CountLimitInputs {
  showCountLimit: boolean | undefined;
  countLimit: number | undefined;
  countLimitOptions: number[] | undefined;
  setCountLimit: (value: number) => void;
}

interface SubheaderFiltersProps {
  subheaderLoading: boolean;
  flowStatusInputs: FlowStatusInputs;
  altFlowStatusInputs: AltFlowStatusInputs;
  searchInputs: SearchInputs;
  altSearchInputs: AltSearchInputs;
  customElement: JSX.Element | undefined;
  pareDownInputs: PareDownInputs;
  groupByInputs: GroupByInputs;
  sortByInputs: SortByInputs;
  sortArrowInputs: SortArrowInputs;
  extraDropdownElements: JSX.Element[];
  countLimitInputs: CountLimitInputs;
  showDateRange?: boolean;
  extraPivotParamsConfig?: Record<string, ExtraPivotParam>;
  extraPivotParamsOptions?: Record<string, Option[]>;
  extraPivotParamsSelections?: Record<string, string | number | null>;
  setExtraPivotParamsSelection?: (payload: { key: string; value: string | number | null }) => void;
}

export const SubheaderFilters = ({
  subheaderLoading,
  flowStatusInputs,
  altFlowStatusInputs,
  searchInputs,
  altSearchInputs,
  customElement,
  pareDownInputs,
  groupByInputs,
  sortByInputs,
  sortArrowInputs,
  extraDropdownElements,
  countLimitInputs,
  showDateRange,
  extraPivotParamsConfig,
  extraPivotParamsOptions,
  extraPivotParamsSelections,
  setExtraPivotParamsSelection,
}: SubheaderFiltersProps) => {
  const { showFlowStatus, flowStatusOptions, flowStatus, updateFlowStatus } = flowStatusInputs;
  const { showAltFlowStatus, altFlowStatusOptions, altFlowStatus, updateAltFlowStatus } = altFlowStatusInputs;
  const { showSearch, search, searchReturn, updateSearchString } = searchInputs;
  const { showAltSearch, altSearch, updateAltSearchString } = altSearchInputs;
  const { showPareDown, selections, options, setPareDownSelections } = pareDownInputs;
  const { showGroupBy, groupBy, onChangeGroup, setGroupBySelection } = groupByInputs;
  const { showSortBy, sortBy, onChangeSort, setSortBySelection } = sortByInputs;
  const { showSortArrow, setSortByDirection } = sortArrowInputs;
  const { showCountLimit, countLimit, countLimitOptions, setCountLimit } = countLimitInputs;
  const [searchValue, setSearchValue] = useState(search);
  const [altSearchVal, setAltSearchVal] = useState(altSearch);
  const debounceUpdateSearch = debounce(updateSearchString, 200);
  const debounceUpdateAltSearch = debounce(updateAltSearchString, 200);

  // Track previous Redux values so we can detect *external* clears
  const prevSearchRef = useRef(search);
  const prevAltSearchRef = useRef(altSearch);
  /**
   * Keep the local search input in sync when Redux clears it "from the side".
   *
   * Example: a workflow change fires setActiveWorkflowKey, which zeroes out
   * subheader.search in Redux. That doesn't automatically clear our local
   * input state (searchValue), since this component is intentionally
   * decoupled via debouncing.
   *
   * This effect:
   *  - Only runs when Redux `search` actually changes
   *  - Only resets local input when Redux goes: "non-empty" -> ""
   *  - Does *not* run while the user is typing, because in that case
   *    Redux is lagging behind the local value (debounce), not the other way around.
   */
  useEffect(() => {
    const prev = prevSearchRef.current;
    // only when Redux search goes from non-empty → empty (e.g. workflow clear)
    if (prev !== search && search === '') {
      setSearchValue('');
    }
    prevSearchRef.current = search;
  }, [search]);

  useEffect(() => {
    const prev = prevAltSearchRef.current;
    if (prev !== altSearch && altSearch === '') {
      setAltSearchVal('');
    }
    prevAltSearchRef.current = altSearch;
  }, [altSearch]);

  const onSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (searchReturn) {
        searchReturn(event.currentTarget.value);
      }
      debounceUpdateSearch(event.currentTarget.value);
      setSearchValue(event.currentTarget.value);
    },
    [searchReturn, debounceUpdateSearch],
  );
  const onClearSearch = () => {
    if (searchReturn) {
      searchReturn('');
    }
    updateSearchString('');
    setSearchValue('');
  };
  const onAltSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (searchReturn) {
        searchReturn(event.currentTarget.value);
      }
      debounceUpdateAltSearch(event.currentTarget.value);
      setAltSearchVal(event.currentTarget.value);
    },
    [searchReturn, debounceUpdateAltSearch],
  );

  const onClearAltSearch = () => {
    if (searchReturn) {
      searchReturn('');
    }
    updateAltSearchString('');
    setAltSearchVal('');
  };

  const onGroupByChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const position = numbro(event.currentTarget.dataset.position);
      const currentSelection = groupBy.selection;
      if (position.value() !== currentSelection) {
        setGroupBySelection(position.value());
        if (onChangeGroup) {
          onChangeGroup();
        }
      }
    },
    [groupBy.selection, onChangeGroup, setGroupBySelection],
  );

  const onSortByChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const position = numbro(event.currentTarget.dataset.position);
      setSortBySelection(position.value());
      if (onChangeSort) {
        onChangeSort();
      }
    },
    [onChangeSort, setSortBySelection],
  );

  const onSortArrowChange = useCallback(() => {
    setSortByDirection();
    if (onChangeSort) {
      onChangeSort();
    }
  }, [onChangeSort, setSortByDirection]);

  const onCountLimitChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const position = numbro(event.currentTarget.dataset.position);
      setCountLimit((countLimitOptions || [])[position.value()]);
    },
    [countLimitOptions, setCountLimit],
  );
  return (
    <React.Fragment>
      <section className="flow-search-filters">
        {showFlowStatus && (
          <SubheaderCheckboxes
            flowStatusOptions={flowStatusOptions}
            flowStatus={flowStatus}
            handleValuesUpdate={updateFlowStatus}
            qaPrefix="subheader"
            show={showFlowStatus}
          />
        )}
        {showAltFlowStatus && (
          <SubheaderCheckboxes
            flowStatusOptions={altFlowStatusOptions}
            flowStatus={altFlowStatus}
            handleValuesUpdate={updateAltFlowStatus}
            qaPrefix="subheader"
            show={showAltFlowStatus}
          />
        )}
        <fieldset className="search">
          {showSearch && (
            <div style={{ position: 'relative' }}>
              <OutlinedInput
                aria-label="Search this view"
                className="form-control"
                fullWidth={true}
                inputProps={{
                  'data-qa': 'SubheaderSearchInput',
                }}
                endAdornment={
                  <IconButton size={'small'} onClick={onClearSearch}>
                    <Close />
                  </IconButton>
                }
                value={searchValue}
                onChange={onSearchChange}
                placeholder="Search this view..."
              />
            </div>
          )}
          {showAltSearch && (
            <div style={{ position: 'relative' }}>
              <OutlinedInput
                aria-label="Search this view"
                className="form-control"
                fullWidth={true}
                inputProps={{
                  'data-qa': 'SubheaderAltSearchInput',
                }}
                endAdornment={
                  <IconButton size={'small'} onClick={onClearAltSearch}>
                    <Close />
                  </IconButton>
                }
                value={altSearchVal}
                onChange={onAltSearchChange}
                placeholder="Search this view..."
              />
            </div>
          )}
        </fieldset>
      </section>
      {!subheaderLoading && (
        <section className="dropdown-filters">
          {/* The custom element is used to inject the floorset selector and range picker in certain views */}
          {showDateRange && customElement}
          {!isEmpty(extraPivotParamsOptions) &&
            !isEmpty(extraPivotParamsSelections) &&
            setExtraPivotParamsSelection &&
            !isEmpty(extraPivotParamsConfig) &&
            Object.keys(extraPivotParamsConfig).map((key) => {
              const config = extraPivotParamsConfig[key];
              if (isNil(config)) {
                return;
              }
              const selectedValue = extraPivotParamsSelections[key];
              const dropdownOptions = extraPivotParamsOptions[key];
              // Find the index of the selected value in options to pass to dropdown
              const selectedIndex = dropdownOptions.findIndex((option) => option.value === selectedValue);

              return (
                <SubheaderDropdown
                  key={key}
                  label={config.text} // Configured 'text' from config for the label
                  options={dropdownOptions as unknown as TenantConfigViewItem[]}
                  selection={selectedIndex}
                  handleChangeOnDropdown={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    const indexValue = e.target.value ?? null;
                    const index = indexValue !== null ? indexValue : null;
                    if (index !== null && dropdownOptions[index as unknown as number]) {
                      // Use index to get value to store in state
                      const selectedOption = dropdownOptions[index as unknown as number];
                      const value = selectedOption.value;
                      setExtraPivotParamsSelection({ key, value });
                    } else {
                      // Not sure this would get hit but a safeguard in case?
                      setExtraPivotParamsSelection({ key, value: null });
                    }
                  }}
                />
              );
            })}

          {showPareDown && (
            <SubheaderDropdownMulti
              selections={selections}
              options={options}
              label={'Refine'}
              qaKey={'refine-pare-down'}
              handleChangeOnClick={setPareDownSelections}
            />
          )}
          {showGroupBy && (
            <SubheaderDropdown
              {...groupBy}
              label={'Group By'}
              qaKey={'Group By'}
              handleChangeOnDropdown={onGroupByChange}
            />
          )}
          {extraDropdownElements}
          {showCountLimit && !isEmpty(countLimitOptions) && (
            <SubheaderDropdown
              label={'Limit'}
              selection={(countLimitOptions || []).findIndex((option) => option === countLimit)}
              options={(countLimitOptions || []).map((i) => ({ dataIndex: i.toString(), text: i.toString() }))}
              handleChangeOnDropdown={onCountLimitChange}
              dataQa={'limit'}
            />
          )}
          {showSortBy && (
            <SubheaderDropdown
              {...sortBy}
              label={'Sort By'}
              qaKey={'Sort By'}
              handleChangeOnDropdown={onSortByChange}
            />
          )}
          {showSortArrow && (
            <MuiThemeProvider theme={muiTheme}>
              <SubheaderQuickActionButton
                iconClass={`far fa-arrow-${sortBy.direction === 'asc' ? 'down' : 'up'}`}
                defaultsPresent={false}
                buttonDataQa="sort-direction-select"
                onClick={onSortArrowChange}
              />
            </MuiThemeProvider>
          )}
        </section>
      )}
    </React.Fragment>
  );
};
